<script setup lang="ts">
/**
 * Ask: a question in plain language → an answer grounded in the knowledge
 * base, with numbered citations that link to their sources. Follow-ups keep
 * the conversation (the earlier turns go along); the options pick what is
 * searched and how many sources the answer may use; "How this answer was
 * made" shows the search, the cut-offs and the model. "Helpful / wrong" goes
 * to the feedback tool so bad answers become fixes and eval cases.
 */
import { BookOpen, ChevronDown, CornerDownLeft, FileText, Info, Loader, RotateCcw, Settings2, Sparkles, ThumbsDown, ThumbsUp } from "@lucide/vue";
import { computed, nextTick, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import type { AskResponse } from "@/api/contracts";
import { ask, uriToPath, useAskConfig, useKnowledgeSchema, useVisibleSources } from "@/api/knowledge";
import { callTool } from "@/api/me";
import CopyButton from "@/components/common/CopyButton.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import MarkdownView from "@/components/common/MarkdownView.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { formatDuration, formatNumber, formatUsd } from "@/lib/format";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Ask", layout: "narrow", requires: { capability: "knowledge.read" } } });

interface Turn {
  id: number;
  question: string;
  answer?: AskResponse;
  error?: unknown;
  pending: boolean;
  rated?: "helpful" | "wrong";
  /** Scope used, for the "how" panel. */
  scope: string;
}

const route = useRoute();
const { can } = useSession();
const config = useAskConfig();
const schema = useKnowledgeSchema();
const knowledgeSources = useVisibleSources();

const question = ref(typeof route.query.q === "string" ? route.query.q : "");
const turns = ref<Turn[]>([]);
const busy = ref(false);
const showOptions = ref(false);
let nextId = 1;

// --- options

const scope = ref<"all" | "pages" | "documents">("all");
const lineage = ref<string | undefined>(undefined);
const sourceId = ref<string | undefined>(undefined);
const count = ref<string | undefined>(undefined);
const followUps = ref(true);

const SCOPES = [
  { value: "all", label: "Everything (pages and documents)" },
  { value: "pages", label: "Knowledge pages (systems, solutions, runbooks, …)" },
  { value: "documents", label: "Documents (Confluence, GitHub, SharePoint, uploads, …)" },
];
const LINEAGES = [
  { value: "smake", label: "smake" },
  { value: "strauss-eu", label: "strauss-eu" },
  { value: "strauss-us", label: "strauss-us" },
];
const sourceOptions = computed(() => (knowledgeSources.data.value ?? []).map((s) => ({ value: s.value, label: `${s.label ?? s.value} (${s.count})` })));
const countOptions = computed(() => {
  const max = config.data.value?.maxSources ?? 20;
  const def = config.data.value?.defaults.sources ?? 8;
  return [...new Set([3, 5, def, 8, 12, max])].filter((n) => n <= max).sort((a, b) => a - b).map((n) => ({ value: String(n), label: `${n} sources${n === def ? " (default)" : ""}` }));
});
const effectiveCount = computed(() => Number(count.value ?? config.data.value?.defaults.sources ?? 8));
const entityTypes = computed(() => (schema.data.value?.types ?? []).map((x) => x.type));
const optionsChanged = computed(() => scope.value !== "all" || Boolean(lineage.value) || Boolean(sourceId.value) || Boolean(count.value) || !followUps.value);

function scopeLabel(): string {
  const parts = [SCOPES.find((s) => s.value === scope.value)!.label.replace(/ \(.*$/, "")];
  if (lineage.value) parts.push(lineage.value);
  if (sourceId.value) parts.push(sourceOptions.value.find((s) => s.value === sourceId.value)?.label ?? "one source");
  return parts.join(" · ");
}

function resetOptions() {
  scope.value = "all";
  lineage.value = undefined;
  sourceId.value = undefined;
  count.value = undefined;
  followUps.value = true;
}

// --- asking

const history = computed(() => turns.value.filter((x) => x.answer && x.answer.sources.length).map((x) => ({ question: x.question, answer: x.answer!.answer })));

async function submit(text = question.value) {
  const q = text.trim();
  if (!q || busy.value) return;
  const turn: Turn = { id: nextId++, question: q, pending: true, scope: scopeLabel() };
  const earlier = followUps.value ? history.value : [];
  turns.value = [...turns.value, turn];
  question.value = "";
  busy.value = true;
  await nextTick();
  document.getElementById(`turn-${turn.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  try {
    turn.answer = await ask({
      question: q,
      ...(scope.value === "documents" ? { types: ["document"] } : scope.value === "pages" && entityTypes.value.length ? { types: entityTypes.value } : {}),
      ...(lineage.value ? { lineages: [lineage.value] } : {}),
      ...(sourceId.value ? { sources: [sourceId.value] } : {}),
      ...(count.value ? { limit: Number(count.value) } : {}),
      ...(earlier.length ? { history: earlier } : {}),
    });
  } catch (err) {
    turn.error = err;
  } finally {
    turn.pending = false;
    turns.value = [...turns.value];
    busy.value = false;
  }
}

function newConversation() {
  turns.value = [];
  question.value = "";
}

async function rate(turn: Turn, kind: "helpful" | "wrong") {
  try {
    await callTool("feedback", {
      kind,
      message: kind === "helpful" ? `Helpful answer to: ${turn.question}` : `Wrong or incomplete answer to: ${turn.question}`,
      subject: turn.question,
      context: {
        answer: turn.answer?.answer.slice(0, 2000),
        citations: turn.answer?.citations.map((c) => c.uri),
        sources: turn.answer?.sources.map((s) => ({ uri: s.uri, relevance: s.relevance, cited: s.cited })),
        retrieval: turn.answer?.retrieval,
        model: turn.answer?.model,
      },
    });
    turn.rated = kind;
    turns.value = [...turns.value];
    toast.success(kind === "helpful" ? "Thanks!" : "Thanks — the team will look at it.");
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    void submit();
  }
}

// --- citations: [n] in the answer → link to source n of that turn

const CITE = /\[(\d{1,2}(?:\s*[,–-]\s*\d{1,2})*)\](?!\()/g;
function linkCitations(turn: Turn): string {
  const answer = turn.answer!;
  const max = answer.sources.length;
  return answer.answer.replace(CITE, (whole, group: string) => {
    const numbers: number[] = [];
    for (const part of group.split(/\s*,\s*/)) {
      const range = /^(\d+)\s*[–-]\s*(\d+)$/.exec(part);
      const [from, to] = range ? [Number(range[1]), Number(range[2])] : [Number(part), Number(part)];
      for (let n = from; n <= Math.min(to, from + 20); n++) numbers.push(n);
    }
    if (!numbers.length || numbers.some((n) => n < 1 || n > max)) return whole;
    return numbers.map((n) => `[\\[${n}\\]](#src-${turn.id}-${n})`).join("");
  });
}

const flashed = ref<string | null>(null);
function onAnswerClick(e: MouseEvent) {
  const a = (e.target as HTMLElement).closest("a");
  const href = a?.getAttribute("href");
  if (!href?.startsWith("#src-")) return;
  e.preventDefault();
  const id = href.slice(1);
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  flashed.value = id;
  window.setTimeout(() => flashed.value === id && (flashed.value = null), 1600);
}

const pct = (x: number) => `${Math.round(x * 100)}%`;
const sourceKindLabel = (s: AskResponse["sources"][number]) => (s.kind === "document" ? (s.source?.name ?? "Document") : (s.type ?? "Page"));

onMounted(() => {
  if (question.value) void submit();
});
</script>

<template>
  <div>
    <PageHeader :title="t.knowledge.askTitle" :description="t.knowledge.askDescription" />

    <!-- How Ask works, in one line -->
    <p v-if="config.data.value" class="mb-4 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
      <Info class="size-3.5" aria-hidden="true" />
      <template v-if="config.data.value.available">
        Searches the knowledge base (keywords + meaning + names, reranked), gives the best
        <strong class="font-medium text-foreground">{{ effectiveCount }}</strong> matches above
        <strong class="font-medium text-foreground">{{ pct(config.data.value.defaults.minRelevance) }}</strong> relevance to
        <strong class="font-medium text-foreground">{{ config.data.value.route?.[0] ? `${config.data.value.route[0].model}` : config.data.value.task }}</strong>,
        which answers only from them.
      </template>
      <template v-else>No AI model is set up for answers — use search instead.</template>
      <RouterLink v-if="can('admin.settings')" :to="{ path: '/admin/settings', query: { q: 'knowledge.ask' } }" class="text-primary hover:underline">Settings</RouterLink>
      <RouterLink v-if="can('admin.ai')" :to="{ path: '/admin/ai', query: { tab: 'routes', task: config.data.value.task } }" class="text-primary hover:underline">Model</RouterLink>
    </p>

    <!-- Conversation -->
    <div class="flex flex-col gap-6">
      <article v-for="turn in turns" :id="`turn-${turn.id}`" :key="turn.id" class="flex scroll-mt-20 flex-col gap-3" aria-live="polite">
        <p class="self-end rounded-2xl rounded-br-sm bg-primary px-4 py-2 text-sm whitespace-pre-wrap text-primary-foreground">{{ turn.question }}</p>
        <div v-if="turn.pending" class="flex items-center gap-2 text-sm text-muted-foreground"><Loader class="size-4 animate-spin" /> Looking through the knowledge base…</div>
        <ErrorState v-else-if="turn.error" compact :error="turn.error" @retry="submit(turn.question)" />
        <div v-else-if="turn.answer" class="rounded-xl border bg-card p-4 sm:p-5">
          <p class="mb-2 flex items-center gap-1.5 text-xs font-medium text-ai">
            <Sparkles class="size-3.5" /> Answer (AI, from the knowledge base)
            <CopyButton class="ml-auto" :value="turn.answer.answer" label="Copy answer" />
          </p>
          <div @click="onAnswerClick"><MarkdownView :source="linkCitations(turn)" /></div>

          <div v-if="turn.answer.sources.length" class="mt-4 border-t pt-3">
            <p class="eyebrow mb-2">{{ t.knowledge.sources_ }} <span class="font-normal normal-case">— {{ turn.answer.citations.length }} of {{ turn.answer.sources.length }} cited</span></p>
            <ol class="flex flex-col gap-1">
              <li
                v-for="s in turn.answer.sources"
                :id="`src-${turn.id}-${s.n}`"
                :key="s.uri"
                :class="cn('grid scroll-mt-24 grid-cols-[1.75rem_1fr_auto] items-start gap-x-2 rounded-md px-1 py-1 text-sm transition-colors', !s.cited && 'opacity-60', flashed === `src-${turn.id}-${s.n}` && 'bg-primary/10')"
              >
                <span class="pt-0.5 text-xs text-muted-foreground tabular-nums">[{{ s.n }}]</span>
                <span class="min-w-0">
                  <span class="flex min-w-0 items-center gap-1.5">
                    <component :is="s.kind === 'document' ? FileText : BookOpen" class="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <RouterLink v-if="uriToPath(s.uri)" :to="uriToPath(s.uri)!" class="truncate text-primary hover:underline">{{ s.title }}</RouterLink>
                    <span v-else class="truncate">{{ s.title }}</span>
                    <span class="shrink-0 text-xs text-muted-foreground">{{ sourceKindLabel(s) }}</span>
                    <span v-if="!s.cited" class="shrink-0 text-xs text-muted-foreground">· not cited</span>
                  </span>
                  <details class="group">
                    <summary class="cursor-pointer list-none text-xs text-muted-foreground hover:text-foreground"><ChevronDown class="inline size-3 transition group-open:rotate-180" /> Matched passage</summary>
                    <p class="mt-1 rounded bg-muted/50 px-2 py-1 text-xs whitespace-pre-wrap text-muted-foreground">{{ s.snippet }}</p>
                  </details>
                </span>
                <span class="flex items-center gap-1.5 pt-1" :title="`Relevance ${pct(s.relevance)}`">
                  <span class="h-1.5 w-12 overflow-hidden rounded-full bg-muted" aria-hidden="true"><span class="block h-full rounded-full bg-primary/70" :style="{ width: pct(s.relevance) }" /></span>
                  <span class="w-8 text-right text-xs text-muted-foreground tabular-nums">{{ pct(s.relevance) }}</span>
                </span>
              </li>
            </ol>
          </div>

          <details class="mt-3 border-t pt-3 text-xs text-muted-foreground">
            <summary class="cursor-pointer font-medium hover:text-foreground">How this answer was made</summary>
            <dl class="mt-2 grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1">
              <dt>Searched for</dt>
              <dd class="break-words text-foreground">"{{ turn.answer.retrieval.query }}"<span v-if="turn.answer.retrieval.query !== turn.question" class="text-muted-foreground"> (follow-up: with the previous question)</span></dd>
              <dt>Scope</dt>
              <dd>{{ turn.scope }}</dd>
              <dt>Search</dt>
              <dd>{{ turn.answer.retrieval.legs.join(" + ") || "—" }}<template v-if="turn.answer.retrieval.reranker">, reranked by {{ turn.answer.retrieval.reranker }}</template></dd>
              <dt>Sources</dt>
              <dd>
                found {{ turn.answer.retrieval.found }} (up to {{ turn.answer.retrieval.limit }}), used {{ turn.answer.retrieval.used }}<template v-if="turn.answer.retrieval.dropped">, {{ turn.answer.retrieval.dropped }} below {{ pct(turn.answer.retrieval.minRelevance) }} relevance left out</template>
              </dd>
              <dt>Text per source</dt>
              <dd>up to {{ formatNumber(turn.answer.retrieval.contextChars) }} characters: the matching passage, its neighbours and the page summary</dd>
              <dt>Conversation</dt>
              <dd>{{ turn.answer.retrieval.historyTurns ? `${turn.answer.retrieval.historyTurns} earlier question${turn.answer.retrieval.historyTurns === 1 ? "" : "s"} included` : "this question only" }}</dd>
              <template v-if="turn.answer.model">
                <dt>Model</dt>
                <dd>{{ turn.answer.model.provider }} / {{ turn.answer.model.model }} (route {{ turn.answer.model.task }})</dd>
              </template>
              <template v-if="turn.answer.usage">
                <dt>Tokens</dt>
                <dd>{{ formatNumber(turn.answer.usage.inputTokens) }} in, {{ formatNumber(turn.answer.usage.outputTokens) }} out<template v-if="turn.answer.usage.costUsd !== undefined"> · {{ formatUsd(turn.answer.usage.costUsd) }}</template></dd>
              </template>
              <dt>Time</dt>
              <dd>{{ formatDuration(turn.answer.durationMs) }}</dd>
            </dl>
          </details>

          <div class="mt-3 flex items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
            <span>Was this right?</span>
            <Button size="xs" variant="ghost" :disabled="Boolean(turn.rated)" :aria-pressed="turn.rated === 'helpful'" @click="rate(turn, 'helpful')"><ThumbsUp /> Yes</Button>
            <Button size="xs" variant="ghost" :disabled="Boolean(turn.rated)" :aria-pressed="turn.rated === 'wrong'" @click="rate(turn, 'wrong')"><ThumbsDown /> No</Button>
          </div>
        </div>
      </article>
    </div>

    <!-- Composer -->
    <form :class="cn('flex flex-col gap-2', turns.length ? 'sticky bottom-0 mt-6 bg-background/95 pt-2 pb-4 backdrop-blur' : 'mb-8')" @submit.prevent="submit()">
      <div class="relative rounded-xl border bg-card shadow-xs focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30">
        <Textarea
          v-model="question"
          :rows="turns.length ? 2 : 3"
          :placeholder="turns.length && followUps ? 'Ask a follow-up…' : t.knowledge.askPlaceholder"
          aria-label="Your question"
          class="resize-none border-0 bg-transparent px-4 py-3 text-base shadow-none focus-visible:ring-0 dark:bg-transparent"
          @keydown="onKeydown"
        />
        <div class="flex flex-wrap items-center justify-between gap-2 px-3 pb-3">
          <span class="flex items-center gap-1 text-xs text-muted-foreground"><CornerDownLeft class="size-3" /> Enter to ask · Shift+Enter for a new line</span>
          <div class="flex items-center gap-1">
            <Button v-if="turns.length" type="button" size="sm" variant="ghost" @click="newConversation"><RotateCcw /> New</Button>
            <Button type="button" size="sm" variant="ghost" :aria-expanded="showOptions" :class="optionsChanged && 'text-primary'" @click="showOptions = !showOptions"><Settings2 /> Options</Button>
            <Button type="submit" :disabled="!question.trim() || busy"><Loader v-if="busy" class="animate-spin" /><Sparkles v-else /> {{ t.knowledge.askButton }}</Button>
          </div>
        </div>
      </div>

      <div v-if="showOptions" class="grid gap-3 rounded-xl border bg-card p-3 sm:grid-cols-2">
        <div class="grid gap-1.5">
          <Label for="ask-scope">Search in</Label>
          <SimpleSelect id="ask-scope" :model-value="scope" :options="SCOPES" @update:model-value="(v) => v && (scope = v as typeof scope)" />
        </div>
        <div class="grid gap-1.5">
          <Label for="ask-count">Sources per answer</Label>
          <SimpleSelect id="ask-count" v-model="count" :options="countOptions" allow-empty :empty-label="`Default (${config.data.value?.defaults.sources ?? 8})`" />
        </div>
        <div class="grid gap-1.5">
          <Label for="ask-lineage">Lineage</Label>
          <SimpleSelect id="ask-lineage" v-model="lineage" :options="LINEAGES" allow-empty empty-label="All lineages" />
          <p v-if="lineage" class="text-xs text-muted-foreground">Only knowledge pages tagged with this lineage; documents have no lineage and are left out.</p>
        </div>
        <div class="grid gap-1.5">
          <Label for="ask-source">Knowledge source</Label>
          <SimpleSelect id="ask-source" v-model="sourceId" :options="sourceOptions" allow-empty empty-label="All sources" />
        </div>
        <div class="flex items-center justify-between gap-3 sm:col-span-2">
          <label class="flex items-center gap-2 text-sm">
            <Switch v-model="followUps" aria-label="Follow-ups" />
            Follow-ups use the conversation <span class="text-xs text-muted-foreground">(last {{ config.data.value?.defaults.history ?? 4 }} questions)</span>
          </label>
          <Button v-if="optionsChanged" type="button" size="sm" variant="ghost" @click="resetOptions">Reset</Button>
        </div>
      </div>

      <div v-if="turns.length === 0" class="flex flex-wrap gap-2">
        <button v-for="ex in t.knowledge.askExamples" :key="ex" type="button" class="rounded-full border bg-card px-3 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground" @click="submit(ex)">
          {{ ex }}
        </button>
      </div>
    </form>
  </div>
</template>
